import { ReactNode, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';
import RegisterForm from '../../forms/register-form';
import PublicLayout from '../../components/public-layout';
import { confirmEmailChange } from '../../api/auth';
import { useNotifications } from '../../notifications';
import LoadingSpinner from '../../components/loading-spinner';
import * as paths from '../../routing/paths';
import styles from './style.module.scss';

const ConfirmByToken = () => {
  const { token } = useParams();
  const confirmationToken = token;
  const [, { error: notifyError }] = useNotifications();
  const [isCheckingReconfirmation, setIsCheckingReconfirmation] = useState(true);
  const [linkError, setLinkError] = useState<string | null>(null);

  useEffect(() => {
    if (!confirmationToken) {
      setIsCheckingReconfirmation(false);
      return;
    }

    confirmEmailChange(confirmationToken).then(async (result) => {
      if (result.emailReconfirmed) {
        window.location.href = paths.account;
      } else if (result.error) {
        // Token was recognized but invalid (cancelled, expired, already used)
        setLinkError(result.error);
        setIsCheckingReconfirmation(false);
      } else {
        // Genuine new registration token
        setIsCheckingReconfirmation(false);
      }
    });
  }, [confirmationToken]);

  if (isCheckingReconfirmation) {
    return <LoadingSpinner />;
  }

  if (linkError) {
    return (
      <Wrapper
        titleMessage={{ id: 'title.link-invalid', defaultMessage: 'Link Invalid' }}
        className={styles['register-form-wrapper']}
        confirmationToken={confirmationToken}
      >
        <p>{linkError}</p>
      </Wrapper>
    );
  }

  return (
    <Wrapper
      titleMessage={confirmationToken ? { id: 'title.register', defaultMessage: 'Welcome to Propdocs' } : undefined}
      descriptionMessage={confirmationToken && `Create an account`}
      className={styles['register-form-wrapper']}
      confirmationToken={confirmationToken}
    >
      <RegisterForm confirmationToken={confirmationToken} />
    </Wrapper>
  );
};

interface WrapperProps {
  children?: ReactNode;
  titleMessage?: MessageDescriptor;
  descriptionMessage?: string;
  className?: string;
  confirmationToken?: string;
}

const Wrapper = ({
  children,
  titleMessage = { id: 'title.register', defaultMessage: 'Create your free account' },
  descriptionMessage = '',
  className,
  confirmationToken,
}: WrapperProps) => {
  const { formatMessage } = useIntl();
  return (
    <PublicLayout mainContentClassName={className} confirmationToken={confirmationToken}>
      <Helmet title={formatMessage(titleMessage)} />
      <h1 data-testid="sign-up" className="mb-0">
        {<FormattedMessage {...titleMessage} />}
      </h1>
      {descriptionMessage && (
        <p data-testid="sign-up-note" className="mb-4 font-weight-normal">
          {descriptionMessage}
        </p>
      )}
      {children}
    </PublicLayout>
  );
};

export default ConfirmByToken;
